import { useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function useDeepDive() {
  const [dive, setDive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [currentIdea, setCurrentIdea] = useState(null);

  const fetchDive = useCallback(async (idea) => {
    setLoading(true);
    setError("");
    setDive(null);
    setCurrentIdea(idea);
    try {
      const { data } = await api.post("/ideas/deep-dive", { idea });
      setDive(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Deep dive failed";
      setError(detail);
      toast.error("Deep dive failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const regenerateReality = useCallback(async () => {
    if (!currentIdea || !dive) return;
    setRegenerating(true);
    try {
      const { data } = await api.post("/ideas/reality-check", {
        idea: currentIdea,
        current_dive: dive,
      });
      setDive((prev) => (prev ? { ...prev, reality_check: data } : prev));
      toast.success("Reality Check regraded");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Regrade failed";
      toast.error(detail);
    } finally {
      setRegenerating(false);
    }
  }, [currentIdea, dive]);

  const shareCurrent = useCallback(async () => {
    if (!currentIdea || !dive) {
      toast.error("Open a deep dive first, then share it.");
      return null;
    }
    try {
      const { data } = await api.post("/ideas/share", {
        idea: currentIdea,
        dive,
      });
      // `share_url` returns the bot-friendly /api/share/<id>/link path so social
      // scrapers see OpenGraph tags. Humans clicking it are meta-refreshed to the
      // clean SPA route. That's the URL we copy to clipboard.
      const full = `${window.location.origin}${data.share_url}`;

      const tryCopy = async () => {
        try {
          await navigator.clipboard.writeText(full);
          return true;
        } catch (_) {
          return false;
        }
      };

      const copied = await tryCopy();
      if (copied) {
        toast.success("Share link copied to clipboard", { description: full });
      } else {
        // Fallback for iOS Safari / insecure contexts: persistent toast with a
        // retry action AND the URL visible so users can long-press to copy.
        toast.message("Copy this share link", {
          description: full,
          duration: 30000,
          action: {
            label: "Copy",
            onClick: async () => {
              const ok = await tryCopy();
              toast[ok ? "success" : "error"](
                ok ? "Copied" : "Long-press the URL above to copy",
              );
            },
          },
        });
      }
      return full;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Share failed";
      toast.error(detail);
      return null;
    }
  }, [currentIdea, dive]);

  const reset = useCallback(() => {
    setDive(null);
    setError("");
    setLoading(false);
    setRegenerating(false);
    setCurrentIdea(null);
  }, []);

  return {
    dive,
    loading,
    error,
    regenerating,
    fetchDive,
    regenerateReality,
    shareCurrent,
    reset,
  };
}
